import React, { useRef, useState } from "react";
import { Undo2, Save } from "lucide-react";
import { ScenariosPill } from "./ScenariosPill";
import { useAuth, canEdit } from "../contexts/AuthContext";
import { fmtPct } from "../lib/formatters";

// Custom slider — 50%-scale thumb + click-side stepping + drag-to-adjust
// + keyboard control (arrow keys step by `step`; hold Shift for 10× step).
// Radix/native <input type="range"> jump-to-position behavior is intentionally
// overridden so clicking left of the thumb decrements by `step` and right of
// the thumb increments by `step`. Dragging the thumb still moves continuously.
function CustomSlider({ min, max, step, value, onChange, testid, onDragChange, color = "#1C4E80" }) {
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [focused, setFocused] = useState(false);

  const decimals = (() => {
    const s = String(step);
    return s.includes(".") ? s.split(".")[1].length : 0;
  })();
  const clamp = (v) => Math.min(max, Math.max(min, v));
  const snap = (v) => {
    const steps = Math.round((v - min) / step);
    return +(min + steps * step).toFixed(decimals);
  };

  const range = max - min;
  const pctThumb = range > 0 ? ((value - min) / range) * 100 : 0;

  const valueFromClientX = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    const raw = min + ((clientX - rect.left) / rect.width) * range;
    return clamp(snap(raw));
  };

  const handleTrackPointerDown = (e) => {
    // Fires only when the pointer went down on the track (thumb stops
    // propagation). Increment or decrement by exactly one `step`.
    const rect = trackRef.current.getBoundingClientRect();
    const clickPct = ((e.clientX - rect.left) / rect.width) * 100;
    const next = clickPct < pctThumb ? value - step : value + step;
    onChange(clamp(snap(next)));
    // Grab focus so keyboard adjust works right after a click.
    trackRef.current?.focus();
  };

  const handleThumbPointerDown = (e) => {
    e.stopPropagation();
    setDragging(true);
    onDragChange && onDragChange(true);
    trackRef.current?.focus();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) { /* ignore */ }
  };

  const handleThumbPointerMove = (e) => {
    if (!dragging) return;
    onChange(valueFromClientX(e.clientX));
  };

  const handleThumbPointerUp = (e) => {
    setDragging(false);
    onDragChange && onDragChange(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_) { /* ignore */ }
  };

  const handleKeyDown = (e) => {
    const mult = e.shiftKey ? 10 : 1;
    let delta = 0;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") delta = step * mult;
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown") delta = -step * mult;
    else if (e.key === "Home") return onChange(clamp(snap(min))), e.preventDefault();
    else if (e.key === "End") return onChange(clamp(snap(max))), e.preventDefault();
    else if (e.key === "PageUp") delta = step * 10;
    else if (e.key === "PageDown") delta = -step * 10;
    else return;
    e.preventDefault();
    onChange(clamp(snap(value + delta)));
  };

  return (
    <div
      ref={trackRef}
      onPointerDown={handleTrackPointerDown}
      onKeyDown={handleKeyDown}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      tabIndex={0}
      data-testid={testid}
      role="slider"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      style={{
        position: "relative",
        height: 16,
        cursor: "pointer",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        WebkitTapHighlightColor: "transparent",
        touchAction: "none",
        outline: "none",
        overflow: "visible",
      }}
    >
      {/* base track */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: 0,
          right: 0,
          height: 2,
          transform: "translateY(-50%)",
          background: "#E8ECF1",
          borderRadius: 1,
        }}
      />
      {/* filled portion */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: 0,
          height: 1,
          width: `${pctThumb}%`,
          transform: "translateY(-50%)",
          background: color,
          borderRadius: 1,
          pointerEvents: "none",
        }}
      />
      {/* Thumb hit target — visible thumb is 7px but iOS finger targets
          need ≥24px per HIG. Wrap the visible thumb in an invisible 28px
          hit area so a finger-press ALWAYS lands on the thumb (not the
          track). Prevents iOS Safari from interpreting the press as a
          text-select gesture on the surrounding row (Dave 2026-08-04). */}
      <div
        onPointerDown={handleThumbPointerDown}
        onPointerMove={handleThumbPointerMove}
        onPointerUp={handleThumbPointerUp}
        onPointerCancel={handleThumbPointerUp}
        data-testid={`${testid}-thumb`}
        style={{
          position: "absolute",
          top: "50%",
          left: `${pctThumb}%`,
          width: 28,
          height: 28,
          transform: "translate(-50%, -50%)",
          background: "transparent",
          cursor: dragging ? "grabbing" : "grab",
          touchAction: "none",
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
          WebkitTapHighlightColor: "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Visible thumb (7px) — decoupled from the hit target so we
            can grow the touch area without changing the design. */}
        <div
          style={{
            width: 10,
            height: 10,
            background: color,
            borderRadius: "50%",
            border: "1px solid #fff",
            boxShadow: focused
              ? "0 0 0 2px #EA6A47"
              : `0 0 0 1px ${color}`,
            transition: "box-shadow 100ms",
            pointerEvents: "none",
          }}
        />
      </div>
    </div>
  );
}

// Bridges the panel's stored-value convention (decimal for %-style variables)
// to the slider's display-unit convention (integer/tenth in %-space).
// All current sliders store a decimal and display as a percent, so we always
// scale by 100 in both directions.
function LabeledSlider({ label, valueKey, min, max, step, format, testid, value, onChange, scale = 100, last = false, dim = false, rowTestid, lightLabel = false }) {
  const displayValue = value * scale;
  const [dragging, setDragging] = useState(false);
  const [recent, setRecent] = useState(false);
  const timer = useRef(null);
  const handleChange = (v) => {
    onChange(valueKey, v / scale);
    setRecent(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setRecent(false), 900);
  };
  const active = dragging || recent;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const decimals = (String(step).split(".")[1] || "").length;
  const commitDraft = () => {
    setEditing(false);
    const n = parseFloat(String(draft).replace(/[%\s,]/g, ""));
    if (!Number.isFinite(n)) return;
    const clamped = Math.min(max, Math.max(min, n));
    const snapped = Number((Math.round(clamped / step) * step).toFixed(decimals));
    if (snapped !== Number(displayValue.toFixed(decimals))) handleChange(snapped);
  };
  return (
    <div
      className="grid grid-cols-[130px_1fr_68px] gap-2 items-center py-1 transition-opacity"
      style={{ borderBottom: last ? undefined : "1px solid #C9D6E2", opacity: dim ? 0.4 : 1 }}
      data-testid={rowTestid}
    >
      <div className={"text-[10px] tracking-[0.09em] uppercase font-semibold " + (lightLabel ? "text-[#A3ADB8]" : "text-[#3F4B57]")}>
        {label}
      </div>
      <CustomSlider
        min={min}
        max={max}
        step={step}
        value={displayValue}
        onChange={handleChange}
        testid={testid}
        onDragChange={setDragging}
        color={lightLabel ? "#A3ADB8" : undefined}
      />
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") setEditing(false);
          }}
          className="w-full text-right text-[11px] font-mono tabular-nums text-ink font-semibold border border-navy px-1 py-0 outline-none bg-white"
          title={`${min} to ${max}, step ${step}`}
          data-testid={`${testid}-value-input`}
        />
      ) : (
      <div
        className="text-right text-[10.5px] font-mono tabular-nums text-ink cursor-text select-none"
        onDoubleClick={() => { setDraft(displayValue.toFixed(decimals)); setEditing(true); }}
        title="Double-click to type a value"
        style={{
          fontWeight: active ? 800 : 600,
          transform: active ? "scale(2)" : "scale(1)",
          transformOrigin: "right center",
          color: active ? "#1C4E80" : lightLabel ? "#A3ADB8" : undefined,
          transition: "transform 120ms ease-out, color 120ms",
        }}
        data-active={active ? "true" : "false"}
        data-testid={`${testid}-value`}
      >
        {format(value)}
      </div>
      )}
    </div>
  );
}

// Interactive scenario-variables panel — sliders + Undo/Save.
// Continues visually from ResultsAtAGlance inside the boxed navy border.
export default function ScenarioVariablesPanel({
  variables,
  originalVariables,
  onChange,
  onUndo,
  onSave,
  activeScenarioName,
  scenarioCount,
  onOpenHistory,
}) {
  const { user } = useAuth();
  const showWriteButtons = canEdit(user);

  if (!variables) return null;

  const dirty =
    !!originalVariables &&
    Object.keys(originalVariables).some(
      (k) => Math.abs((variables[k] ?? 0) - (originalVariables[k] ?? 0)) > 1e-9,
    );

  return (
    <div
      className="attention-border flex flex-col"
      data-testid="scenario-variables-panel"
      style={{
        background: "#fff",
        minHeight: 0,
        /* 2026-02-15 (Dave) — hardcoded `10px 12px` replaced with the same
           clamp() tokens the other panels use via `.panel-compact`. At
           1920w the old fixed 10px was 3.4px SHORTER than the responsive
           padding on adjacent panels, which pulled this header's coral
           underline up by ~3.4px and broke horizontal alignment with the
           Elements and Funding Level panels. */
        padding: "var(--u-panel-pad-y) var(--u-panel-pad-x)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <div
        className="dash-panel-header"
        style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", columnGap: "0.75rem" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="text-[12px] tracking-[0.14em] uppercase font-bold text-navy shrink-0" data-testid="sv-title">
            Scenario Variables
          </div>
        </div>
        {/* Undo / Save appear only once a variable has changed (Dave 2026-10-12). */}
        <div className="flex items-center gap-1.5" style={{ position: "relative", left: 9 }}>
          {!dirty ? null : showWriteButtons ? (
            <>
              <button
                onClick={onUndo}
                disabled={!dirty}
                data-testid="sv-undo"
                className="flex items-center gap-1 border border-rule bg-white text-navy hover:border-navy px-2 py-0 leading-[16px] text-[11px] font-semibold rounded-full disabled:opacity-40 disabled:cursor-not-allowed"
                title="Revert to baseline"
              >
                <Undo2 size={11} /> Undo
              </button>
              <button
                onClick={onSave}
                disabled={!dirty}
                data-testid="sv-save"
                data-write-action="true"
                className="flex items-center gap-1 bg-navy text-white hover:bg-[#12406B] px-2 py-0 leading-[16px] text-[11px] font-semibold rounded-full disabled:opacity-40 disabled:cursor-not-allowed"
                title="Save as new scenario"
              >
                <Save size={11} /> Save
              </button>
            </>
          ) : (
            /* View-only users can move sliders freely (safe sandbox) but
               cannot persist. Show only the Undo button so they can revert
               their exploration back to baseline. Save is hidden entirely. */
            <button
              onClick={onUndo}
              disabled={!dirty}
              data-testid="sv-undo"
              className="flex items-center gap-1 border border-rule bg-white text-navy hover:border-navy px-2 py-0 leading-[16px] text-[11px] font-semibold rounded-full disabled:opacity-40 disabled:cursor-not-allowed"
              title="Revert to baseline (view-only sandbox — no changes are saved)"
            >
              <Undo2 size={11} /> Undo
            </button>
          )}
        </div>
        <div className="justify-self-end">
          <ScenariosPill
            scenarioCount={scenarioCount}
            activeScenarioName={activeScenarioName}
            onOpenHistory={onOpenHistory}
          />
        </div>
      </div>

      {/* Slider stack spaces evenly when the panel gets extra vertical room. */}
      <div className="flex-1 flex flex-col justify-around min-h-0">
      <LabeledSlider
        label="Reserve Fund Target"
        valueKey="target_pct"
        min={0}
        max={100}
        step={1}
        format={(v) => fmtPct(v * 100, 0)}
        testid="slider-target_pct"
        value={variables.target_pct ?? 0}
        onChange={onChange}
      />
      <LabeledSlider
        label="Year 1 Adjustment"
        valueKey="adjustment_pct"
        min={-100}
        max={100}
        step={1}
        format={(v) => fmtPct(v * 100, 0)}
        testid="slider-adjustment_pct"
        value={variables.adjustment_pct ?? 0}
        onChange={onChange}
      />

      {/* Adjustment Period — whole years; min is 1 when a Y1 adjustment exists, else 0. */}
      {(() => {
        const hasAdjustment = (variables.adjustment_pct ?? 0) !== 0;
        return (
          <LabeledSlider
            label="Adjustment Period"
            valueKey="adjustment_period"
            min={hasAdjustment ? 1 : 0}
            max={30}
            step={1}
            scale={1}
            format={(v) => `${v} ${v === 1 ? "Year" : "Years"}`}
            testid="slider-adjustment_period"
            rowTestid="adjustment-period-row"
            value={variables.adjustment_period ?? 0}
            onChange={hasAdjustment ? onChange : () => {}}
          />
        );
      })()}
      <LabeledSlider
        label="Inflation Rate"
        valueKey="inflation_rate"
        min={0}
        max={10}
        step={0.1}
        format={(v) => fmtPct(v * 100, 1)}
        testid="slider-inflation_rate"
        value={variables.inflation_rate ?? 0}
        onChange={onChange}
      />
      <LabeledSlider
        label="Interest Earned"
        valueKey="interest_rate"
        min={0}
        max={10}
        step={0.05}
        format={(v) => fmtPct(v * 100, 2)}
        testid="slider-interest_rate"
        last
        value={variables.interest_rate ?? 0}
        onChange={onChange}
      />
      </div>
    </div>
  );
}
